/**
 * Formats a JSON string with specified indentation space.
 * @param {string} json - The JSON string to format.
 * @param {number} space - Number of spaces for indentation.
 * @returns {string} - The formatted JSON string.
 */
export const formatJson = (json, space = 2) => {
    try {
        const parsed = JSON.parse(json);
        return JSON.stringify(parsed, null, space);
    } catch (e) {
        throw new Error(e.message);
    }
};

/**
 * Minifies a JSON string.
 * @param {string} json - The JSON string to minify.
 * @returns {string} - The minified JSON string.
 */
export const minifyJson = (json) => {
    try {
        const parsed = JSON.parse(json);
        return JSON.stringify(parsed);
    } catch (e) {
        throw new Error(e.message);
    }
};

/**
 * Validates a JSON string.
 * @param {string} json - The JSON string to validate.
 * @returns {boolean} - True if valid, false otherwise.
 */
export const isValidJson = (json) => {
    try {
        JSON.parse(json);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Escapes a JSON string.
 * @param {string} json - The JSON string to escape.
 * @returns {string} - The escaped JSON string.
 */
export const escapeJson = (json) => {
    try {
        return JSON.stringify(json);
    } catch (e) {
        throw new Error(e.message);
    }
};

/**
 * Unescapes a JSON string.
 * @param {string} json - The escaped JSON string to unescape.
 * @returns {string} - The unescaped JSON string.
 */
// Helper for basic string unescaping
const naiveUnescape = (json) => {
    return json
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\b/g, '\b')
        .replace(/\\f/g, '\f');
};

// Helper to recursively parsing string values that look like JSON
const recursiveUnescape = (data) => {
    if (typeof data === 'string') {
        try {
            // Try to parse the string to see if it's actually valid JSON
            const parsed = JSON.parse(data);
            if (typeof parsed === 'object' && parsed !== null) {
                return recursiveUnescape(parsed);
            }
        } catch (e) {
            // Not valid strict JSON
            // Try naive unescape to clean up escaped characters anyway
            const unescaped = naiveUnescape(data);
            if (unescaped !== data) {
                // If naive unescape changed something, see if the result is now valid JSON object
                try {
                    const parsed = JSON.parse(unescaped);
                    if (typeof parsed === 'object' && parsed !== null) {
                        return recursiveUnescape(parsed);
                    }
                } catch (e2) {
                    // Still not an object, but return the cleaned string
                }
                return unescaped;
            }
        }
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(item => recursiveUnescape(item));
    }

    if (typeof data === 'object' && data !== null) {
        const result = {};
        for (const key in data) {
            result[key] = recursiveUnescape(data[key]);
        }
        return result;
    }

    return data;
};

export const unescapeJson = (json) => {
    try {
        const parsed = JSON.parse(json);
        // If the initial parse works, deeply check for embedded JSON in strings
        return recursiveUnescape(parsed);
    } catch (e) {
        // Try parsing assuming the input is the content of a string (missing outer quotes)
        try {
            const parsed = JSON.parse(`"${json}"`);
            // Also recursively unescape this result
            return recursiveUnescape(parsed);
        } catch (e2) {
            // Fallback: Naive replacement for basic escapes
            const unescaped = naiveUnescape(json);

            // Try to see if this unescaped string can be parsed as JSON now
            try {
                const parsed = JSON.parse(unescaped);
                if (typeof parsed === 'object' && parsed !== null) {
                    return recursiveUnescape(parsed);
                }
            } catch (e3) {
                // ignore
            }

            return unescaped;
        }
    }
};


